import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Activity, ChevronDown, ChevronRight, Download, Headphones } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { downloadCsv } from "@/lib/schema";

// Flatten data (and data.extra) into rows for the detail view
function flatten(data) {
  const rows = [];
  Object.entries(data || {}).forEach(([k, v]) => {
    if (k === "extra" && v && typeof v === "object") Object.entries(v).forEach(([ek, ev]) => rows.push([`extra.${ek}`, ev]));
    else rows.push([k, v]);
  });
  return rows;
}
const show = (v) => (v !== null && typeof v === "object" ? JSON.stringify(v) : String(v));

export default function TelemetryAdmin() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [days, setDays] = useState(90);
  const { user } = useAuth();
  const isClinician = user?.role === "therapist";

  const load = async () => {
    try { const { data } = await api.get("/telemetry/recent?limit=100"); setEvents(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const exportCsv = () => downloadCsv(api, { days }, `analytics-${days}d.csv`).then(() => toast.success("Exported"))
    .catch(() => toast.error("Export failed"));

  return (
    <div>
      <PageHeader
        eyebrow={isClinician ? "My patients" : "Live stream"}
        title="Analytics"
        description="Everything the headsets report, newest first. Click a row to see every value it carried, including extra measures. Use Export CSV to analyse the full history (not just the last 100) in a spreadsheet."
        action={
          <div className="flex items-center gap-2">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-9 rounded-md border border-input bg-transparent text-sm px-2" data-testid="export-days">
              {[7, 30, 90, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
            </select>
            <Button variant="outline" onClick={exportCsv} data-testid="export-analytics-csv"><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
          </div>
        }
      />
      <div className="panel rounded-2xl">
        <div className="px-4 py-3 border-b border-purple-100 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.15em] text-purple-700 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            Live · last 100 events
          </div>
          <div className="text-xs text-slate-500 tabular-nums">{events.length} events</div>
        </div>
        <div className="divide-y divide-purple-50 max-h-[72vh] overflow-y-auto">
          {loading ? <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
          : events.length === 0 ? (
            <div className="text-center py-16">
              <Activity className="h-8 w-8 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
              <div className="text-sm text-slate-500">No analytics yet</div>
              <div className="text-xs text-slate-400 mt-2">They appear as soon as a headset reports a session.</div>
            </div>
          ) : events.map((e) => {
            const d = e.data || {};
            const isOpen = !!open[e.id];
            const detail = flatten(d);
            const level = d.level ?? d.to_level;
            return (
              <div key={e.id} data-testid={`telemetry-event-${e.id}`}>
                <button type="button" onClick={() => setOpen({ ...open, [e.id]: !isOpen })}
                  className="w-full text-left px-4 py-3 grid grid-cols-12 gap-3 items-center hover:bg-purple-50/30">
                  <div className="col-span-3 text-xs text-slate-500 tabular-nums flex items-center gap-1">
                    {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    {new Date(e.created_at).toLocaleString()}
                  </div>
                  <div className="col-span-2">
                    <Badge variant="outline" className="font-mono text-[10px] chip-brand border-0">{e.event_type}</Badge>
                    {e.test && <Badge variant="outline" className="ml-1 text-[10px] bg-sky-50 text-sky-800 border-sky-200">test</Badge>}
                  </div>
                  <div className="col-span-3 min-w-0 flex items-center gap-2">
                    <Headphones className="h-3.5 w-3.5 text-purple-600 flex-shrink-0" strokeWidth={1.75} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{e.headset?.name || e.headset?.serial || e.headset_serial}</div>
                      <div className="text-[11px] text-slate-500 truncate">{e.patient?.name ? `Patient: ${e.patient.name}` : "Unassigned"}</div>
                    </div>
                  </div>
                  <div className="col-span-2 text-xs text-slate-600 truncate">
                    {(e.simulation?.name || d.game) ? <span>{e.simulation?.name || d.game}{level !== undefined ? ` · L${level}` : ""}</span> : <span className="text-slate-400">—</span>}
                  </div>
                  <div className="col-span-2 text-right text-xs text-slate-600 tabular-nums">
                    {d.outcome ? <span className="mr-2">{d.outcome}</span> : null}
                    {e.duration_seconds ? `${Math.round(e.duration_seconds / 60)} min` : ""}
                    {e.completion_pct ? <span className="ml-2 text-purple-700 font-medium">{e.completion_pct}%</span> : ""}
                  </div>
                </button>
                {isOpen && (
                  <div className="px-6 pb-4 bg-slate-50/60" data-testid={`telemetry-detail-${e.id}`}>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 py-2">Everything this event carried</div>
                    <table className="w-full text-xs">
                      <tbody>
                        {detail.map(([k, v]) => (
                          <tr key={k} className="border-t border-slate-200">
                            <td className="py-1 pr-4 font-mono text-slate-500 w-56 align-top">{k}</td>
                            <td className="py-1 text-slate-800 break-all">{show(v)}</td>
                          </tr>
                        ))}
                        {detail.length === 0 && <tr><td className="py-1 text-slate-400">No extra values.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
